/**
 * Sources: your own documents, the ones a source check may search and cite.
 *
 * A workspace-level dialog rather than a per-exploration panel, because the corpus
 * is workspace-level: you paste your lab notes once and they ground every idea you
 * open afterwards.
 *
 * Two things this surface is careful about. It refuses in numbers — every refusal
 * states the arithmetic, because "too large" with no limit visible leaves the user
 * to guess, and the obvious guess is to delete everything. And it says once, in the
 * right place, that this text is sent to whichever provider the selected model
 * belongs to.
 *
 * A paper named by DOI or arXiv id is the third way in, and the only one that
 * fetches: the press is what makes the request, `engine/paper.ts` makes it, and
 * what comes back goes through `addDoc` like everything else here — same caps,
 * same refusals, same list. What it adds to the two above is that the document
 * arrives with a line saying how much of the paper is in it, because a document
 * fetched on somebody's behalf is the one they did not read on the way in.
 *
 * A file chosen or dropped here lands in the SAME two fields a paste lands in, and
 * goes no further. That is the entire argument for taking PDFs at all: this dialog
 * refused them for years on the grounds that a SILENTLY mangled document is worse
 * than none, and what answers that objection is not a better extractor — it is that
 * the extraction arrives in a box, under a printed count of what it lost, and
 * somebody reads it before pressing Add. The user's review IS the admission gate,
 * and `addDoc`'s own gates are untouched behind it.
 */

import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import {
  CORPUS_CHARS,
  DOC_CHARS,
  MAX_DOCS,
  addDoc,
  corpusChars,
  leadClaim,
  listDocs,
  loadDoc,
  removeDoc,
  SOURCES_DOOR,
  type CorpusDoc,
  type LoadedDoc,
} from '../core/corpus'
import { claimQuery, retrieve, type Retrieval } from '../core/retrieve'
import { ACCEPTED, readSourceFile } from '../core/sourceFile'
import { activeHost, useStore } from '../core/store'
import type { ReasonNode } from '../core/types'
import { calendarDay } from '../core/dates'
import { SOURCES } from '../core/sources'
import { PAPER_NOT_A_REFERENCE, addPaper, parsePaperRef, type PaperReading } from '../engine/paper'
import { Icon } from './components/Icon'
import { Switch } from './components/Controls'
import { useModalFocus } from './components/useModalFocus'

/** The reader's calendar day — see `core/dates.ts`. */
const fmtDay = calendarDay

/**
 * What "Try the search" shows: the retrieval a check on this claim runs.
 *
 * Through `claimQuery`, the check's own query builder, because this used to run
 * the pasted text as the query while the check ran something else, and so it
 * promised passages the checks were never shown. A node picked from the
 * exploration brings its label, which makes the preview exactly the check on that
 * node; a pasted claim has none, and is the check on a node whose label adds
 * nothing to its claim. Editing a picked claim makes it a pasted one, so a label
 * cannot outlive the words it was the label of.
 *
 * Over the user's documents alone. With research rows on, a check also ranks
 * what they fetched in the same pool — records that do not exist until the check
 * runs, and which no preview can show.
 *
 * Exported for the test that holds it to the check itself.
 */
export function previewRetrieval(
  probe: string,
  picked: Pick<ReasonNode, 'label' | 'claim'> | undefined,
  docs: LoadedDoc[],
): Retrieval {
  return retrieve(claimQuery(picked?.claim === probe ? picked.label : '', probe), docs)
}

/** "Added “journal-club.pdf” — 2 passages." The half of every add that always
 *  works, in one form whichever press made it. */
export function addedLine(doc: { title: string; passages: number }): string {
  return `Added “${doc.title}” — ${doc.passages} passage${doc.passages === 1 ? '' : 's'}.`
}

/**
 * What landed, in a sentence.
 *
 * The MACHINE half of the provenance line is `reading`, and this is the only place
 * it becomes English — so a document that arrived as an abstract is never reported
 * as a paper, however much the title looks like one. `engine/paper.ts` writes the
 * same fact into the document's first line, where a source check reads it; this is
 * for the person standing at the dialog.
 */
const READING_NOTE: Record<PaperReading, string> = {
  'full-text': 'the paper’s own full text',
  abstract: 'the abstract only — no open-access full text was there to fetch',
  metadata: 'the record only — neither an abstract nor a full text was available',
}

export function SourcesDialog() {
  const open = useStore((s) => s.sourcesOpen)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  useModalFocus(dialogRef, open)
  const setOpen = useStore((s) => s.setSources)
  const preferCorpus = useStore((s) => s.preferCorpus)
  const setPreferCorpus = useStore((s) => s.setPreferCorpus)
  const toast = useStore((s) => s.toast)
  // The host of the SELECTED provider, because that is where the next check would
  // send these passages. Naming one provider permanently was true only while there
  // was one.
  const host = useStore((s) => activeHost(s))
  const proposeSeed = useStore((s) => s.proposeSeed)
  // The two settings the paper door needs, read HERE because this is the layer
  // that holds a preference: `fetchPaper` is handed the rows somebody has on and
  // never re-decides what they switched off — `LiveFetchOptions.rows`' own rule.
  const sourcesOn = useStore((s) => s.sourcesOn)
  const mailto = useStore((s) => s.sourceMailto)

  const [docs, setDocs] = useState<CorpusDoc[]>([])
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [confirming, setConfirming] = useState<string | null>(null)
  const [probe, setProbe] = useState('')
  const [probeNode, setProbeNode] = useState<string | null>(null)
  // Only while the dialog is open, so a pass streaming into the graph does not
  // re-render a closed dialog on every token.
  const session = useStore((s) => (s.sourcesOpen ? s.session : null))
  /** The claims a check could run on: every node but the seed, which is the
   *  question and is refused a check. */
  const checkable = useMemo(
    () =>
      session
        ? session.order
            .map((id) => session.nodes[id])
            .filter((n): n is ReasonNode => !!n && n.parentId !== null)
        : [],
    [session],
  )
  const picked = probeNode ? checkable.find((n) => n.id === probeNode) : undefined
  /** The typed reference, whether a fetch is in flight, and what the last press
   *  had to say. The note is a `warn` for every refusal — not a reference, a row
   *  switched off, a service that did not answer, a corpus cap — and an ordinary
   *  note for the document that landed. It stays on screen rather than becoming a
   *  toast for the reason the extraction arithmetic does: it says what the new
   *  document IS, and three seconds is not long enough to read that and act on it. */
  const [paperRef, setPaperRef] = useState('')
  const [fetching, setFetching] = useState(false)
  const [paperNote, setPaperNote] = useState<{ warn: boolean; text: string } | null>(null)
  /** What a file read had to say for itself. `fileErr` is a refusal — a format
   *  this cannot read, a PDF that will not parse, the files a multi-drop left
   *  behind. `note` is the extraction arithmetic, which is not an error and must
   *  stay on screen for as long as the text it describes is in the box. */
  const [fileErr, setFileErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [reading, setReading] = useState(false)
  const [dragging, setDragging] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)
  /** Whether a read is in flight, and which one. Refs rather than the `reading`
   *  state above, because two files released in the same tick both see `false` in
   *  state — it has not re-rendered between them. See `take`. */
  const readInFlight = useRef(false)
  const readGen = useRef(0)
  /** `dragenter`/`dragleave` bubble, so moving the pointer over any child of the
   *  drop target fires a leave on the target itself and a boolean flag flickers
   *  the highlight off mid-drag. Counting enters against leaves is the only thing
   *  that reads a nested subtree correctly. */
  const dragDepth = useRef(0)

  const refresh = () => setDocs(listDocs())
  useEffect(() => {
    if (open) {
      refresh()
      setConfirming(null)
    }
  }, [open])

  const used = useMemo(() => (open ? corpusChars() : 0), [open, docs])

  // A live retrieval against a claim the user types, so the keyword search is
  // legible before it is trusted. This is the honest answer to "will it find the
  // right passage?" — try it and see.
  const found = useMemo(() => {
    if (!open || probe.trim().length < 8) return null
    const loaded = docs.map((d) => loadDoc(d.id)).filter((d): d is NonNullable<typeof d> => !!d)
    return previewRetrieval(probe, picked, loaded)
  }, [open, probe, picked, docs])

  if (!open) return null

  /**
   * Fill the two fields from a file, and stop.
   *
   * Nothing is added, nothing is stored, and Add stays exactly where it was —
   * `addDoc`'s gates all still run against whatever is in the box when it is
   * pressed, including the text the person edited after reading the note. An
   * auto-add here would be the one thing that turns a lossy extraction back into a
   * silent one.
   *
   * `also` is the count of files a drop carried past the first. They are named
   * rather than discarded quietly: two files dropped means two documents intended.
   *
   * ONE READ AT A TIME, held by two mechanisms that are a pair rather than a
   * belt-and-braces habit. The REFUSAL is the honest half and the one somebody
   * sees: the Choose button goes disabled while a read runs, but the whole scrim
   * stays a live drop target, so a second file released mid-extraction used to
   * start a second read — and then the box held whichever finished LAST, in no
   * relation to the order they were dropped, while the first completion cleared
   * `reading` and re-enabled Add over content that was about to be replaced. The
   * GENERATION is the belt for the refusal's own race: whatever gets past the
   * guard, only the newest read may write the fields or clear `reading`, so a
   * stale extraction can never overwrite a newer one or unlock the buttons under
   * it.
   */
  const take = async (file: File, also = 0) => {
    if (readInFlight.current) {
      // Through the inline file-error channel rather than a toast, beside the box
      // it is about: this is a fact about what the box is doing right now.
      setFileErr(
        'A file is already being read — this holds one document at a time. Wait for it to land in the box, then drop the next one.',
      )
      return
    }
    const others =
      also > 0
        ? `${also} other file${also === 1 ? '' : 's'} dropped alongside it ${also === 1 ? 'was' : 'were'} not read — this holds one document at a time, so that what lands in the box is text you can read before it is added. Drop the rest one by one.`
        : ''
    const gen = (readGen.current += 1)
    readInFlight.current = true
    setFileErr(null)
    setNote(null)
    setReading(true)
    try {
      const res = await readSourceFile(file)
      // Overtaken. Say nothing and write nothing: the newer read owns the fields
      // and owns the end of `reading`.
      if (gen !== readGen.current) return
      if ('refused' in res) {
        // The refusal first, then the others — a file that was itself refused must
        // not be reported as the one that got through.
        setFileErr(others ? `${res.refused} ${others}` : res.refused)
        return
      }
      setTitle(res.title)
      setText(res.text)
      // Only a PDF has losses to report. A .txt arrived whole or not at all.
      if (res.kind === 'pdf') setNote(res.note)
      if (others) setFileErr(`“${res.title}” was read. ${others}`)
    } catch {
      /* The rejection nobody was catching. This was a try/finally with no catch at
         all, so anything `readSourceFile` threw rather than refused ended as
         "Reading…" simply stopping: the box unchanged, the buttons re-enabled, and
         not a word about why. `file.text()` rejecting on a file that moved on disk
         between the pick and the read is the live example — found in round one,
         closed here.

         The `refused` shape stays the PRIMARY channel and this is the residue: a
         reader that can phrase the failure always does, and what lands here is the
         failure it could not. Same generation guard as every write above, so a
         stale rejection cannot overwrite a newer read's text with a sentence about
         a file the person has already replaced. Unnamed on purpose — a throw is not
         a fact about which file it was, and `file.name` is empty often enough that
         the sentence would open on a pair of quotation marks around nothing. */
      if (gen !== readGen.current) return
      const failed =
        'That file could not be read — the browser could not finish reading it. Try it again, or paste the text in instead.'
      setFileErr(others ? `${failed} ${others}` : failed)
    } finally {
      if (gen === readGen.current) {
        readInFlight.current = false
        setReading(false)
      }
    }
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    dragDepth.current = 0
    setDragging(false)
    const files = Array.from(e.dataTransfer.files)
    if (files[0]) void take(files[0], files.length - 1)
  }

  /** Add what is in the box and clear it, or refuse out loud. The caller says
   *  that it landed, so each press says so once — see `addAndArgue`. */
  const addFromBox = (): { title: string; passages: number } | null => {
    const res = addDoc(title || text.slice(0, 60), text)
    if (!res.ok) {
      toast(res.why, 'warn')
      return null
    }
    setTitle('')
    setText('')
    setNote(null)
    setFileErr(null)
    refresh()
    return res.doc
  }

  const add = () => {
    const doc = addFromBox()
    if (doc) toast(addedLine(doc), 'info')
  }

  /**
   * Fetch one paper by its identifier, and add it.
   *
   * THE PRESS IS THE ONLY THING THAT FETCHES. Nothing here reaches a service while
   * somebody types, and no keystroke resolves anything: the field is a string until
   * this runs. `parsePaperRef` refuses before any request is made, so a typo costs
   * a sentence rather than a round trip to a public API that asks its callers to be
   * polite.
   *
   * Every refusal on the way arrives in the words of whoever refused — the parser,
   * the service, or `addDoc`'s own arithmetic — and none of them is rewritten here.
   */
  const bringPaper = async () => {
    const parsed = parsePaperRef(paperRef)
    if (!parsed) {
      setPaperNote({ warn: true, text: PAPER_NOT_A_REFERENCE })
      return
    }
    setFetching(true)
    setPaperNote({ warn: false, text: 'Fetching…' })
    try {
      const got = await addPaper(parsed, {
        // Sparse on purpose, so a row nobody has touched takes the registry's own
        // default rather than reading as off.
        rows: SOURCES.filter((s) => s.wired && (sourcesOn[s.id] ?? s.defaultOn)),
        mailto,
      })
      if (!got.ok) {
        setPaperNote({ warn: true, text: got.why })
        return
      }
      setPaperRef('')
      refresh()
      setPaperNote({
        warn: false,
        text: `Added “${got.doc.title}” — ${READING_NOTE[got.reading]}. ${got.doc.passages} passage${
          got.doc.passages === 1 ? '' : 's'
        }, and the document's first line says where each half came from.`,
      })
    } finally {
      setFetching(false)
    }
  }

  /**
   * Add the document and offer its central claim as the next seed.
   *
   * The composition that this tool is actually best at, made native: a research report
   * is a dense, cited, pre-structured document, and the useful thing to do with one is
   * to argue with what it concludes rather than to read it again. Making it the corpus
   * means the passage-citation gate does the grounding — a separate code path from the
   * URL gate, and the stricter of the two, because a passage either is in the document
   * or is not.
   *
   * It fills the idea box and stops. Nothing is spent, the sentence is editable, and
   * Explore is still a separate press — the same rule the frame suggestion follows.
   *
   * The idea box of a NEW exploration. Over a graph that already has a seed the
   * composer is the Ask box, so the lifted claim used to land there as a question
   * about the open exploration — one Enter from being asked of a graph it has
   * nothing to do with. Opened through `newExploration`, the one door to a fresh
   * canvas, so its lock and its refusal are the ordinary ones; the exploration it
   * leaves is saved on the way out, as every door saves it.
   */
  const addAndArgue = () => {
    const claim = leadClaim(text)
    const doc = addFromBox()
    if (!doc) return
    setOpen(false)
    // ONE toast: what was added, and what became of its claim. It was two — the
    // plain Add's line, then "Added. A new exploration is open…" — stacked over
    // the lines the lifted claim had just raised above the composer.
    const added = addedLine(doc)
    const st = useStore.getState()
    const fresh = !claim || st.session.order.length === 0 || st.newExploration()
    if (claim && !fresh) {
      // Refused by the lock (a pass or a live run), which has said so in its own
      // toast. The claim is not put in the Ask box it would otherwise reach.
      toast(
        `${added} Its central claim reads “${claim}” — start a new exploration once this one is free, and paste it there.`,
        'info',
      )
      return
    }
    if (claim) {
      // The composer is the idea box and is always mounted, so the proposal is
      // claimed the moment it is made.
      proposeSeed(claim)
      toast(
        `${added} A new exploration is open with its central claim in the question box — edit it before you explore. Source checks will cite it by passage.`,
        'info',
      )
    } else {
      // Said out loud rather than silently leaving the box empty. The document is
      // added either way, which is the half that always works.
      toast(
        `${added} No sentence in it read as a claim to argue with — state the claim yourself in the idea box. Its passages will still be cited.`,
        'info',
      )
    }
  }

  return (
    <div
      className="scrim"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false)
      }}
      // The whole scrim takes the drop, not just the sheet. A file released over a
      // surface with no handler is a file the BROWSER opens, which navigates away
      // from the app and takes the half-edited box with it — so the target has to
      // be everything the dialog covers, and the highlight below says where the
      // text will land.
      onDragEnter={() => {
        dragDepth.current += 1
        setDragging(true)
      }}
      onDragLeave={() => {
        dragDepth.current -= 1
        if (dragDepth.current <= 0) {
          dragDepth.current = 0
          setDragging(false)
        }
      }}
      onDragOver={(e) => {
        // Without this the drop event never fires at all.
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
      }}
      onDrop={onDrop}
    >
      <div
        className="sheet sheet--tall panel"
        role="dialog"
        aria-modal="true"
        aria-label={SOURCES_DOOR}
        ref={dialogRef}
        tabIndex={-1}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* The door's name and glyph, as the sidebar and the palette draw them
              (D390), so the row pressed and the sheet it opens say one thing. */}
          <Icon name="file" size={18} style={{ color: 'var(--accent)' }} />
          <h2>{SOURCES_DOOR}</h2>
        </div>

        <div className={`sheet-body scroll${dragging ? ' sheet-body--drop' : ''}`}>
          <p className="gnote">
            Paste or drop documents you trust — notes, a paper, a spec, a transcript. A source check
            will search them alongside the web and cite them by passage, so the graph can rest on
            your material rather than on the model's memory.
          </p>
          {/* Where the words go, said the way it happens: the documents are kept
              in this browser, and a passage leaves only inside the request of a
              pass that retrieved it — a source check, a panel or a link scan
              (`retrieveFor` in engine/reason.ts). "Sent … as part of the request"
              read as though a document went out when it was added. */}
          <p className="gnote gnote--warn">
            Your documents stay in this browser. A passage is sent to {host} only when a source
            check, panel or link scan uses it, inside that request. Do not paste anything you would
            not send.
          </p>

          {docs.length > 0 && (
            <ul className="docs">
              {docs.map((d) => (
                <li key={d.id}>
                  <div className="docs-row">
                    <b>{d.title}</b>
                    <span className="srcs-meta">
                      {d.passages} passage{d.passages === 1 ? '' : 's'} · {d.chars.toLocaleString()}{' '}
                      characters · added {fmtDay(d.addedAt)}
                    </span>
                  </div>
                  {confirming === d.id ? (
                    <div className="docs-confirm">
                      <span>
                        Remove it? Checks that cited it keep their quotations — they were copied at
                        the time — but the link to the document goes.
                      </span>
                      <button
                        className="btn"
                        onClick={() => {
                          removeDoc(d.id)
                          setConfirming(null)
                          refresh()
                        }}
                      >
                        Remove
                      </button>
                      <button className="btn" onClick={() => setConfirming(null)}>
                        Keep
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn"
                      style={{ color: 'var(--text-3)' }}
                      onClick={() => setConfirming(d.id)}
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <p className="gnote gnote--quiet">
            {docs.length} of {MAX_DOCS} documents · {used.toLocaleString()} of{' '}
            {CORPUS_CHARS.toLocaleString()} characters. One document can be up to{' '}
            {DOC_CHARS.toLocaleString()}.
          </p>

          <div className="field">
            <span className="eyebrow">Add a document</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                className="input"
                style={{ flex: 1, minWidth: 0 }}
                value={title}
                disabled={reading}
                placeholder="What to call it"
                onChange={(e) => setTitle(e.target.value)}
                aria-label="Document title"
              />
              <button
                className="btn"
                disabled={reading}
                onClick={() => fileRef.current?.click()}
                title={`Reads ${ACCEPTED.join(', ')} into the box below. Nothing is added until you press Add.`}
              >
                {reading ? 'Reading…' : 'Choose a file'}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPTED.join(',')}
                style={{ display: 'none' }}
                aria-hidden
                tabIndex={-1}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  // Cleared before the read, so choosing the SAME file twice fires
                  // change twice — otherwise somebody who fixes the file on disk
                  // and picks it again gets silence.
                  e.target.value = ''
                  if (f) void take(f)
                }}
              />
            </div>
            {fileErr && <p className="gnote gnote--warn">{fileErr}</p>}
            {/* Above the box and in the quiet banner, not in a toast: it is the
                arithmetic of what the extraction lost, and it has to still be
                there while the text underneath it is being read. A toast would
                take it away three seconds in, which is before anybody has read a
                page. */}
            {note && <p className="note">{note}</p>}
            <textarea
              className="textarea"
              rows={7}
              value={text}
              disabled={reading}
              placeholder="Paste plain text or Markdown — or drop a file here. A PDF is extracted on this machine and the extraction is lossy: what it lost is counted above the box, and the text is yours to read and correct before you press Add."
              onChange={(e) => setText(e.target.value)}
              aria-label="Document text"
            />
            {/* Both fields and both actions are shut while a file is being read, for the
                one reason the file button is: the extraction is about to replace both
                fields, so an Add pressed mid-read admits the outgoing draft under the
                outgoing title and then the box changes under the person who pressed it.
                The fields are shut on that same reason turned around — `take`'s
                completion writes both unconditionally, so a field still taking keystrokes
                through the visible "Reading…" is taking them in order to throw them away,
                and whatever was typed or pasted disappears under text nobody has read
                yet. Invalidating the pending read on the first edit instead is declined:
                that trades one silent loss for another, discarding on a stray keypress a
                read the person pressed for. The read is user-initiated and bounded, so
                the interval is short and the button names it for the whole of it. */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn btn--primary"
                disabled={reading || text.trim().length < 40}
                onClick={add}
              >
                <Icon name="plus" size={13} />
                Add
              </button>
              {/* Offered only for something long enough to have a thesis in it. A
                  paragraph of lab notes has no central claim to lift, and a button
                  that mostly fails is worse than one that is not there. */}
              <button
                className="btn"
                disabled={reading || text.trim().length < 400}
                onClick={addAndArgue}
                title={
                  text.trim().length < 400
                    ? 'For a whole report — something with a conclusion in it to argue with.'
                    : 'Adds it, then opens a new exploration with its central claim in the question box. Nothing is spent.'
                }
              >
                <Icon name="spark" size={13} />
                Add and argue with it
              </button>
              {text.length > 0 && (
                <span className="srcs-meta">{text.length.toLocaleString()} characters</span>
              )}
            </div>
            <p className="gnote gnote--quiet">
              Pasting a research report? <b>Add and argue with it</b> adds it to your sources and
              opens a new exploration with its central claim in the question box, so the graph
              argues with a cited synthesis instead of with the open web. The claim it lifts is a
              guess at what the document is for — edit it before you explore.
            </p>
          </div>

          <div className="field">
            <span className="eyebrow">Bring a paper</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                className="input"
                style={{ flex: 1, minWidth: 0 }}
                value={paperRef}
                disabled={fetching}
                placeholder="DOI or arXiv id"
                spellCheck={false}
                onChange={(e) => setPaperRef(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !fetching && paperRef.trim()) void bringPaper()
                }}
                aria-label="DOI or arXiv id"
              />
              <button
                className="btn"
                disabled={fetching || !paperRef.trim()}
                onClick={() => void bringPaper()}
                title="Fetches that one paper into the list above. Nothing is spent."
              >
                {fetching ? 'Fetching…' : 'Add'}
              </button>
            </div>
            {paperNote && (
              <p className={paperNote.warn ? 'gnote gnote--warn' : 'gnote'}>{paperNote.text}</p>
            )}
            <p className="gnote gnote--quiet">
              The open-access full text where there is one, the abstract where there is not, and the
              record alone when there is neither — the document's first line says which, and where
              each half came from. A work Crossref holds a retraction notice for is added marked as
              retracted. arXiv is abstract-only, and no PDF is read. Nothing is spent: these are the
              keyless research rows in Settings, and one switched off there is not called here.
            </p>
          </div>

          {docs.length > 0 && (
            <div className="field">
              <span className="eyebrow">Try the search</span>
              {checkable.length > 0 && (
                <select
                  className="select"
                  value={picked ? picked.id : ''}
                  onChange={(e) => {
                    const n = checkable.find((c) => c.id === e.target.value)
                    setProbeNode(n ? n.id : null)
                    if (n) setProbe(n.claim)
                  }}
                  aria-label="A claim from this exploration"
                >
                  <option value="">A claim you paste</option>
                  {checkable.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.label}
                    </option>
                  ))}
                </select>
              )}
              <textarea
                className="textarea"
                rows={2}
                value={probe}
                placeholder="Paste a claim to see the passages a check on that claim would be shown."
                onChange={(e) => {
                  setProbe(e.target.value)
                  if (picked && e.target.value !== picked.claim) setProbeNode(null)
                }}
                aria-label="Try a claim against your sources"
              />
              {found && (
                <>
                  <p className="gnote">
                    {found.passages.length === 0
                      ? 'Nothing in your sources matched that closely enough to show a check. A check on this claim would search the web only.'
                      : `${found.passages.length} passage${found.passages.length === 1 ? '' : 's'} would be shown, matching ${Math.round(found.bestCoverage * 100)}% of the claim's distinctive terms at best${found.match === 'thin' ? ' — which is a weak match, and a check would be told so' : ''}.`}
                  </p>
                  {found.passages.slice(0, 3).map((p, i) => (
                    <div key={i} className="srcs" style={{ gap: 2 }}>
                      <div>
                        <span className="srcs-meta">
                          {p.docTitle} · passage {p.n}
                        </span>
                        <p className="srcs-quote" data-checked>
                          {p.text.slice(0, 220)}
                          {p.text.length > 220 ? '…' : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          <div className="field">
            <span className="eyebrow">When a check finds a match</span>
            <Switch
              label="Prefer my sources over the web"
              checked={preferCorpus}
              onChange={setPreferCorpus}
              tint="var(--accent)"
            />
            <p className="gnote">
              Off: a check searches the web and your documents together, and cites both. On: when
              your documents match a claim, the check reads only them — cheaper and narrower, and it
              cannot answer the prior-art question, because absence in your documents is not absence
              in the literature.
            </p>
          </div>
        </div>

        <div className="sheet-actions">
          <button className="btn btn--primary" onClick={() => setOpen(false)}>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
